// /admin/crm/contactos — rolodex unificado del CRM.
// Lista todos los contactos (cliente / proveedor / interno / prospecto / otro) con
// filtros por tipo, búsqueda y opción de crear contacto manual. Export CSV.

import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { listarContactos, crearContactoManual, actualizarContacto, TIPOS_CONTACTO } from '../../lib/crmApi'

const FILTROS = [
  { key: 'todos',          label: 'Todos' },
  { key: 'cliente',        label: '🛒 Clientes' },
  { key: 'proveedor',      label: '📦 Proveedores' },
  { key: 'interno',        label: '👤 Internos' },
  { key: 'prospecto',      label: '✨ Prospectos' },
  { key: 'otro',           label: '📌 Otros' },
  { key: 'sin_clasificar', label: '◯ Sin clasificar' },
]

export default function Contactos() {
  const navigate = useNavigate()
  const [filtro, setFiltro] = useState('todos')
  const [q, setQ] = useState('')
  const [contactos, setContactos] = useState([])
  const [cargando, setCargando] = useState(true)
  const [crearOpen, setCrearOpen] = useState(false)

  async function cargar() {
    setCargando(true)
    try {
      const data = await listarContactos({
        tipo: filtro === 'todos' ? null : filtro,
        q,
      })
      setContactos(data)
    } finally {
      setCargando(false)
    }
  }

  useEffect(() => { cargar() }, [filtro])
  useEffect(() => {
    const t = setTimeout(cargar, 250)
    return () => clearTimeout(t)
  }, [q])

  const conteos = useMemo(() => {
    const c = { todos: contactos.length, sin_clasificar: 0 }
    TIPOS_CONTACTO.forEach(t => { c[t.v] = 0 })
    contactos.forEach(ct => {
      if (!ct.tipos || ct.tipos.length === 0) c.sin_clasificar++
      else ct.tipos.forEach(t => { c[t] = (c[t] || 0) + 1 })
    })
    return c
  }, [contactos])

  function exportarCSV() {
    const headers = ['Nombre','Teléfono','Instagram','Email','Tipos','Empresa','Notas']
    const rows = contactos.map(c => [
      c.nombre || '',
      c.telefono_e164 ? '+' + c.telefono_e164 : '',
      c.ig_username ? '@' + c.ig_username : '',
      c.email || '',
      (c.tipos || []).join(' · '),
      c.empresa || '',
      (c.notas || '').replace(/\n/g, ' '),
    ])
    const csv = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n')
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `contactos-limpack-${new Date().toISOString().slice(0,10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <AdminLayout title="Contactos CRM">
      <div className="contactos-page">
        <header className="contactos-head">
          <div>
            <h1>📒 Contactos</h1>
            <p>Rolodex unificado del CRM — clientes, proveedores, internos y prospectos.</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn ghost" onClick={exportarCSV} disabled={contactos.length === 0}>
              ⬇ Export CSV
            </button>
            <button className="btn primary" onClick={() => setCrearOpen(true)}>+ Nuevo contacto</button>
            <Link to="/admin/crm" className="btn ghost">← Volver al CRM</Link>
          </div>
        </header>

        <div className="contactos-filtros">
          {FILTROS.map(f => (
            <button
              key={f.key}
              className={`contactos-chip ${filtro === f.key ? 'is-active' : ''}`}
              onClick={() => setFiltro(f.key)}
            >
              {f.label}
              <span className="contactos-chip-count">{conteos[f.key] || 0}</span>
            </button>
          ))}
        </div>

        <input
          type="text"
          className="contactos-buscar"
          placeholder="Buscar por nombre, teléfono, IG, email, empresa…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />

        {cargando ? (
          <div className="contactos-empty">Cargando…</div>
        ) : contactos.length === 0 ? (
          <div className="contactos-empty">
            No hay contactos {filtro !== 'todos' ? `de tipo "${FILTROS.find(f => f.key === filtro)?.label}"` : ''}.
          </div>
        ) : (
          <div className="contactos-tabla-wrap">
            <table className="contactos-tabla">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Contacto</th>
                  <th>Tipos</th>
                  <th>Empresa</th>
                  <th>Notas</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {contactos.map(c => (
                  <tr key={c.id}>
                    <td><strong>{c.nombre || '—'}</strong></td>
                    <td style={{ fontFamily: 'SF Mono, monospace', fontSize: 12 }}>
                      {c.telefono_e164 && <div>📱 +{c.telefono_e164}</div>}
                      {c.ig_username && <div>📷 @{c.ig_username}</div>}
                      {c.email && <div>✉ {c.email}</div>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {(c.tipos || []).length === 0 ? (
                          <span className="contactos-tag" style={{ background: '#FEF3C7', color: '#92400E' }}>
                            sin clasificar
                          </span>
                        ) : c.tipos.map(t => {
                          const meta = TIPOS_CONTACTO.find(x => x.v === t)
                          if (!meta) return null
                          return (
                            <span key={t} className="contactos-tag" style={{ background: meta.color + '22', color: meta.color }}>
                              {meta.icon} {meta.l}
                            </span>
                          )
                        })}
                      </div>
                    </td>
                    <td style={{ fontSize: 13 }}>{c.empresa || '—'}</td>
                    <td style={{ fontSize: 12, color: '#64748B', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.notas || '—'}
                    </td>
                    <td>
                      <Link to={`/admin/crm?contacto=${c.id}`} className="contactos-link">
                        Ver →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {crearOpen && (
          <NuevoContactoModal
            onClose={() => setCrearOpen(false)}
            onCreado={(c) => { setCrearOpen(false); cargar() }}
          />
        )}

        <Estilos />
      </div>
    </AdminLayout>
  )
}

function NuevoContactoModal({ onClose, onCreado }) {
  const [form, setForm] = useState({
    nombre: '', telefono: '', ig_username: '', email: '',
    tipos: [], empresa: '', notas: ''
  })
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState(null)

  function toggleTipo(t) {
    setForm(f => ({
      ...f,
      tipos: f.tipos.includes(t) ? f.tipos.filter(x => x !== t) : [...f.tipos, t]
    }))
  }

  async function guardar(e) {
    e.preventDefault()
    setGuardando(true); setError(null)
    try {
      if (form.tipos.includes('cliente')) {
        throw new Error('Para vincular a cliente Limpack, hacelo desde una conversación con el botón "Vincular a cliente Limpack".')
      }
      const tel = form.telefono.replace(/[^0-9]/g, '')
      const c = await crearContactoManual({
        nombre: form.nombre.trim() || null,
        telefono: tel || null,
        ig_username: form.ig_username.trim() || null,
        email: form.email.trim() || null,
        tipos: form.tipos,
        empresa: form.empresa.trim() || null,
        notas: form.notas.trim() || null,
      })
      onCreado(c)
    } catch (err) {
      setError(err.message)
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div className="contactos-modal-bg" onClick={onClose}>
      <div className="contactos-modal" onClick={(e) => e.stopPropagation()}>
        <h2>Nuevo contacto</h2>
        <form onSubmit={guardar} className="contactos-form">
          <label>Nombre
            <input value={form.nombre} onChange={(e) => setForm({...form, nombre: e.target.value})} placeholder="Lucía García" />
          </label>
          <label>Teléfono
            <input value={form.telefono} onChange={(e) => setForm({...form, telefono: e.target.value})} placeholder="098610001" />
          </label>
          <label>Instagram (sin @)
            <input value={form.ig_username} onChange={(e) => setForm({...form, ig_username: e.target.value})} placeholder="usuario.ig" />
          </label>
          <label>Email
            <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="contacto@empresa.com" />
          </label>

          <div>
            <label style={{ display: 'block', marginBottom: 6 }}>Tipos</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {TIPOS_CONTACTO.filter(t => t.v !== 'cliente').map(t => (
                <button
                  key={t.v}
                  type="button"
                  onClick={() => toggleTipo(t.v)}
                  className="crm-contacto-tipo-btn"
                  style={{
                    background: form.tipos.includes(t.v) ? t.color : '#fff',
                    color: form.tipos.includes(t.v) ? '#fff' : t.color,
                    borderColor: t.color,
                  }}
                >
                  {t.icon} {t.l}
                </button>
              ))}
            </div>
          </div>

          {form.tipos.includes('proveedor') && (
            <label>Empresa
              <input value={form.empresa} onChange={(e) => setForm({...form, empresa: e.target.value})} placeholder="MercadoPago, Boyerco, NEBA..." />
            </label>
          )}

          <label>Notas
            <textarea value={form.notas} onChange={(e) => setForm({...form, notas: e.target.value})} rows={2} placeholder="Notas internas…" />
          </label>

          {error && <div style={{ color: '#DC2626', fontSize: 12 }}>✗ {error}</div>}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <button type="button" className="btn ghost" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn primary" disabled={guardando}>
              {guardando ? 'Guardando…' : 'Crear contacto'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function Estilos() {
  return (
    <style>{`
      .contactos-page { max-width: 1200px; margin: 0 auto; padding: 24px; font-family: Inter, system-ui, sans-serif; color: #1A3461; }
      .contactos-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; flex-wrap: wrap; margin-bottom: 18px; }
      .contactos-head h1 { margin: 0 0 6px; font-size: 24px; font-weight: 900; letter-spacing: -0.02em; }
      .contactos-head p { margin: 0; font-size: 13px; color: #64748B; }

      .contactos-filtros { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px; }
      .contactos-chip {
        background: #fff; border: 1.5px solid #E2E8F0; border-radius: 999px;
        padding: 6px 12px; font-size: 12.5px; font-weight: 700;
        color: #475569; cursor: pointer; font-family: inherit;
        display: inline-flex; align-items: center; gap: 6px;
      }
      .contactos-chip:hover { border-color: #1A3461; }
      .contactos-chip.is-active { background: #1A3461; color: #fff; border-color: #1A3461; }
      .contactos-chip-count {
        background: rgba(0,0,0,0.08); padding: 1px 7px; border-radius: 999px;
        font-size: 11px; font-weight: 800; min-width: 18px; text-align: center;
      }
      .contactos-chip.is-active .contactos-chip-count { background: rgba(255,255,255,0.2); }

      .contactos-buscar {
        width: 100%; box-sizing: border-box;
        padding: 10px 14px; margin-bottom: 12px;
        font-family: inherit; font-size: 13.5px;
        border: 1.5px solid #E2E8F0; border-radius: 8px;
      }
      .contactos-buscar:focus { outline: none; border-color: #1A3461; box-shadow: 0 0 0 3px #1A346122; }

      .contactos-empty { padding: 60px 20px; text-align: center; color: #94A3B8; font-size: 14px; background: #fff; border-radius: 12px; }

      .contactos-tabla-wrap { background: #fff; border: 1px solid #E2E8F0; border-radius: 12px; overflow: hidden; }
      .contactos-tabla { width: 100%; border-collapse: collapse; font-size: 13px; }
      .contactos-tabla thead { background: #F8FAFC; }
      .contactos-tabla th {
        text-align: left; padding: 10px 14px; font-size: 11px;
        font-weight: 800; color: #64748B; text-transform: uppercase; letter-spacing: 0.06em;
      }
      .contactos-tabla td { padding: 10px 14px; border-top: 1px solid #F1F5F9; vertical-align: top; }
      .contactos-tabla tr:hover { background: #F8FAFC; }

      .contactos-tag {
        display: inline-flex; align-items: center; gap: 3px;
        padding: 2px 8px; border-radius: 999px;
        font-size: 11px; font-weight: 700;
      }

      .contactos-link { color: #1A3461; font-weight: 700; text-decoration: none; font-size: 12.5px; }
      .contactos-link:hover { text-decoration: underline; }

      .contactos-modal-bg {
        position: fixed; inset: 0; background: rgba(15,27,54,0.5);
        display: flex; align-items: center; justify-content: center;
        z-index: 1000; padding: 20px;
      }
      .contactos-modal {
        background: #fff; border-radius: 16px; padding: 24px;
        max-width: 460px; width: 100%; max-height: 90vh; overflow-y: auto;
      }
      .contactos-modal h2 { margin: 0 0 16px; font-size: 18px; font-weight: 800; }
      .contactos-form { display: flex; flex-direction: column; gap: 12px; }
      .contactos-form label { display: block; font-size: 12px; font-weight: 700; color: #475569; }
      .contactos-form input, .contactos-form textarea {
        width: 100%; box-sizing: border-box; margin-top: 4px;
        padding: 8px 12px; font-family: inherit; font-size: 13px;
        border: 1px solid #E2E8F0; border-radius: 6px;
      }
      .contactos-form input:focus, .contactos-form textarea:focus { outline: none; border-color: #1A3461; }
    `}</style>
  )
}
